import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { Button, Menu, Dropdown } from 'antd';
import { MenuOutlined, UserOutlined, GlobalOutlined } from '@ant-design/icons';
import { useLanguage } from '../contexts/LanguageContext';
import en from '../locales/en';
import zh from '../locales/zh';

const NavContainer = styled.nav`
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(10px);
  padding: 0 24px;
  height: 64px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  position: sticky;
  top: 0;
  z-index: 1000;
  box-shadow: 0 2px 8px rgba(25, 118, 210, 0.1);
  
  @media (max-width: 768px) {
    padding: 0 16px;
    height: 56px;
  }
`;

const Logo = styled(Link)`
  color: #1976d2;
  font-size: 24px;
  font-weight: bold;
  text-decoration: none;
  flex-shrink: 0;
  
  @media (max-width: 768px) {
    font-size: 20px;
  }
`;

const DesktopNav = styled.div`
  display: flex;
  align-items: center;
  gap: 24px;
  
  @media (max-width: 768px) {
    display: none;
  }
`;

const MobileNav = styled.div`
  display: none;
  
  @media (max-width: 768px) {
    display: flex;
    align-items: center;
  }
`;

const NavLink = styled(Link)`
  color: ${props => props.$active ? '#1976d2' : '#666'};
  text-decoration: none;
  font-size: 15px;
  padding: 8px;
  transition: all 0.3s;
  white-space: nowrap;
  border-radius: 4px;
  
  &:hover {
    color: #1976d2;
    background: rgba(25, 118, 210, 0.1);
  }
`;

const LoginButton = styled(Button)`
  height: 36px;
  padding: 0 24px;
  font-size: 15px;
  border-radius: 18px;
  background: #1976d2;
  color: #fff;
  border: none;
  transition: all 0.3s ease;

  &:hover {
    background: #1565c0 !important;
    color: #fff !important;
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(25, 118, 210, 0.2);
  }

  @media (max-width: 768px) {
    height: 32px;
    padding: 0 20px;
    font-size: 14px;
  }
`;

const UserButton = styled(Button)`
  height: 36px;
  padding: 0 24px;
  font-size: 15px;
  border-radius: 18px;
  background: rgba(25, 118, 210, 0.1);
  color: #1976d2;
  border: none;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: all 0.3s ease;

  &:hover {
    background: rgba(25, 118, 210, 0.15) !important;
    color: #1976d2 !important;
    transform: translateY(-2px);
  }

  .anticon {
    font-size: 16px;
  }

  @media (max-width: 768px) {
    height: 32px;
    padding: 0 20px;
    font-size: 14px;
  }
`;

const MenuButton = styled(Button)`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  padding: 0;
  border: none;
  background: transparent;
  color: #666;
  border-radius: 6px;
  
  &:hover {
    color: #1976d2;
    background: rgba(25, 118, 210, 0.1);
  }
  
  .anticon {
    font-size: 20px;
  }
`;

const MobileMenu = styled(Menu)`
  &.ant-menu {
    background: #fff;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
    border: 1px solid rgba(25, 118, 210, 0.1);
    min-width: 160px;
    padding: 4px;
    border-radius: 8px;
    
    .ant-menu-item {
      color: #666;
      font-size: 15px;
      padding: 12px 16px;
      height: auto;
      line-height: 1.4;
      margin: 2px 0;
      border-radius: 6px;
      
      &:hover {
        color: #1976d2;
        background: rgba(25, 118, 210, 0.1);
      }
      
      &.ant-menu-item-selected {
        color: #1976d2;
        background: rgba(25, 118, 210, 0.15);
      }
    }
  }
`;

const StyledMenu = styled(Menu)`
  &.ant-dropdown-menu {
    background: #fff;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
    border-radius: 12px;
    padding: 8px;
    border: 1px solid rgba(25, 118, 210, 0.1);
    
    .ant-dropdown-menu-item {
      padding: 8px 16px;
      color: #666;
      border-radius: 8px;
      
      &:hover {
        background: rgba(25, 118, 210, 0.1);
        color: #1976d2;
      }

      &.ant-dropdown-menu-item-selected {
        background: rgba(25, 118, 210, 0.15);
        color: #1976d2;
      }
    }
  }
`;

const LanguageButton = styled(Button)`
  height: 36px;
  width: 36px;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 18px;
  background: rgba(25, 118, 210, 0.1);
  color: #1976d2;
  border: none;
  margin-right: 16px;
  transition: all 0.3s ease;

  &:hover {
    background: rgba(25, 118, 210, 0.15) !important;
    color: #1976d2 !important;
    transform: translateY(-2px);
  }

  .anticon {
    font-size: 16px;
  }

  @media (max-width: 768px) {
    height: 32px;
    width: 32px;
    margin-right: 12px;
  }
`;

function Navbar() {
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileMenuVisible, setMobileMenuVisible] = useState(false);
  const token = localStorage.getItem('token');
  const { currentLanguage, setCurrentLanguage } = useLanguage();
  const t = currentLanguage === 'en' ? en : zh;

  const isActive = (path) => location.pathname === path;

  const handleLogout = () => {
    localStorage.removeItem('token');
    navigate('/login');
  };

  const mobileMenuItems = [
    {
      key: '/',
      label: t.nav.home,
      onClick: () => navigate('/')
    },
    {
      key: '/download',
      label: t.nav.download,
      onClick: () => navigate('/download')
    }
  ];

  if (token) {
    mobileMenuItems.push(
      {
        key: '/profile',
        label: t.nav.profile,
        onClick: () => navigate('/profile')
      },
      {
        key: 'logout',
        label: t.nav.logout,
        onClick: handleLogout
      }
    );
  } else {
    mobileMenuItems.push(
      {
        key: '/login',
        label: t.nav.login,
        onClick: () => navigate('/login')
      }
    );
  }

  const userMenu = (
    <MobileMenu>
      <Menu.Item key="profile" onClick={() => navigate('/profile')}>
        {t.nav.profile}
      </Menu.Item>
      <Menu.Item key="logout" onClick={handleLogout}>
        {t.nav.logout}
      </Menu.Item>
    </MobileMenu>
  );

  const languageMenu = (
    <StyledMenu>
      <Menu.Item 
        key="en" 
        onClick={() => setCurrentLanguage('en')}
        className={currentLanguage === 'en' ? 'ant-dropdown-menu-item-selected' : ''}
      >
        English
      </Menu.Item>
      <Menu.Item 
        key="zh" 
        onClick={() => setCurrentLanguage('zh')}
        className={currentLanguage === 'zh' ? 'ant-dropdown-menu-item-selected' : ''}
      >
        中文
      </Menu.Item>
    </StyledMenu>
  );

  return (
    <NavContainer>
      <Logo to="/">{t.nav.appName}</Logo>
      
      <DesktopNav>
        <NavLink to="/" $active={isActive('/')}>
          {t.nav.home}
        </NavLink>
        <NavLink to="/download" $active={isActive('/download')}>
          {t.nav.download}
        </NavLink>
        <Dropdown overlay={languageMenu} placement="bottomRight">
          <LanguageButton icon={<GlobalOutlined />} />
        </Dropdown>
        {/*token ? (
          <Dropdown overlay={userMenu} placement="bottomRight">
            <UserButton icon={<UserOutlined />}>
              {t.nav.profile}
            </UserButton>
          </Dropdown>
        ) : (
          <LoginButton type="primary" onClick={() => navigate('/login')}>
            {t.nav.login}
          </LoginButton>
        )*/}
      </DesktopNav>

      <MobileNav>
        <Dropdown overlay={languageMenu} placement="bottomRight">
          <LanguageButton icon={<GlobalOutlined />} />
        </Dropdown>
        <Dropdown 
          overlay={<MobileMenu items={mobileMenuItems} />}
          placement="bottomRight"
          trigger={['click']}
          onVisibleChange={setMobileMenuVisible}
        >
          <MenuButton icon={<MenuOutlined />} />
        </Dropdown>
      </MobileNav>
    </NavContainer>
  );
}

export default Navbar; 